import { useEffect, useMemo, useState, type ElementType, type ReactNode } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  InputBase,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import StackedBarChartOutlinedIcon from "@mui/icons-material/StackedBarChartOutlined";
import ShowChartOutlinedIcon from "@mui/icons-material/ShowChartOutlined";
import AreaChartOutlinedIcon from "@mui/icons-material/AreaChartOutlined";
import PieChartOutlinedIcon from "@mui/icons-material/PieChartOutlined";
import GridOnOutlinedIcon from "@mui/icons-material/GridOnOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import DeviceHubOutlinedIcon from "@mui/icons-material/DeviceHubOutlined";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import InsertChartOutlinedIcon from "@mui/icons-material/InsertChartOutlined";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import WidgetsOutlinedIcon from "@mui/icons-material/WidgetsOutlined";
import DashboardCustomizeOutlinedIcon from "@mui/icons-material/DashboardCustomizeOutlined";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import UndoOutlinedIcon from "@mui/icons-material/UndoOutlined";
import RedoOutlinedIcon from "@mui/icons-material/RedoOutlined";
import ZoomInOutlinedIcon from "@mui/icons-material/ZoomInOutlined";
import ZoomOutOutlinedIcon from "@mui/icons-material/ZoomOutOutlined";
import FitScreenOutlinedIcon from "@mui/icons-material/FitScreenOutlined";
import AlignHorizontalLeftOutlinedIcon from "@mui/icons-material/AlignHorizontalLeftOutlined";
import AlignHorizontalCenterOutlinedIcon from "@mui/icons-material/AlignHorizontalCenterOutlined";
import AlignVerticalTopOutlinedIcon from "@mui/icons-material/AlignVerticalTopOutlined";
import FlipToFrontOutlinedIcon from "@mui/icons-material/FlipToFrontOutlined";
import FlipToBackOutlinedIcon from "@mui/icons-material/FlipToBackOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import PhoneAndroidOutlinedIcon from "@mui/icons-material/PhoneAndroidOutlined";
import BookmarkBorderOutlinedIcon from "@mui/icons-material/BookmarkBorderOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import DriveFileRenameOutlineOutlinedIcon from "@mui/icons-material/DriveFileRenameOutlineOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { useTranslation } from "react-i18next";
import { AppButton, AppTextField } from "../form";
import AppIconButton from "../form/AppIconButton";
import WidgetPalette from "../WidgetPalette";
import DecorationPalette, { DecorationPaletteHeader } from "../DecorationPalette";
import AssignedQueriesPanel from "../AssignedQueriesPanel";
import { dashboardQueryService } from "../../services/dashboardQueryService";
import { dashboardService } from "../../services/dashboardService";
import type { DashboardQuery } from "../../types/query";
import type {
  DashboardBookmark,
  DashboardDecorationKind,
  DashboardDecorationPlacement,
  DashboardFilter,
  DashboardPage,
  DashboardVisualPlacement,
  QueryColumnSchema,
  ReportDashboard,
  VizTypeSpec,
} from "../../types/dashboard";
import type { Widget } from "../../types/widget";
import {
  FIELD_DRAG_MIME,
  VISUAL_DRAG_MIME,
  encodeDragField,
} from "../../utils/visualDesignerDrag";
import VisualFormatControls from "../VisualFormatControls";
import type { VisualFormat } from "../../types/dashboard";

export { FIELD_DRAG_MIME, VISUAL_DRAG_MIME };

const VIZ_ICON_BY_TYPE: Record<string, ElementType> = {
  table: TableChartOutlinedIcon,
  column: BarChartOutlinedIcon,
  bar: StackedBarChartOutlinedIcon,
  line: ShowChartOutlinedIcon,
  area: AreaChartOutlinedIcon,
  pie: PieChartOutlinedIcon,
  heatmap: GridOnOutlinedIcon,
  treemap: AccountTreeOutlinedIcon,
  sankey: DeviceHubOutlinedIcon,
  gauge: SpeedOutlinedIcon,
  card: CreditCardOutlinedIcon,
  slicer: FilterAltOutlinedIcon,
};

function vizIcon(vizType: string): ElementType {
  return VIZ_ICON_BY_TYPE[vizType] || InsertChartOutlinedIcon;
}

export type QuerySchemaState = {
  columns: QueryColumnSchema[];
  note?: string;
  loading?: boolean;
};

type DataPaneProps = {
  queries: DashboardQuery[];
  assignedQueryIds: string[];
  selectedQueryId?: string;
  visualQueryId?: string;
  onSelectQuery: (queryId: string) => void;
  schemasByQueryId: Record<string, QuerySchemaState>;
  onUseField: (column: QueryColumnSchema, queryId: string) => void;
  onExplore?: (queryId: string, objectName: string) => void;
  widgets: Widget[];
  placedWidgetIds: Set<string>;
  onAddWidget: (widgetId: string) => void;
  onAddDecoration: (kind: DashboardDecorationKind) => void;
  decorations: DashboardDecorationPlacement[];
  onAssignQuery: (queryId: string) => void | Promise<void>;
  onUnassignQuery: (queryId: string) => void | Promise<void>;
};

const LEFT_NAV_TABS = [
  { id: "data", icon: StorageOutlinedIcon, labelKey: "reportDesigner.data" },
  { id: "widgets", icon: WidgetsOutlinedIcon, labelKey: "reportDesigner.widgets" },
  { id: "components", icon: DashboardCustomizeOutlinedIcon, labelKey: "reportDesigner.components" },
  { id: "queries", icon: HubOutlinedIcon, labelKey: "reportDesigner.assignedQueries" },
] as const;

export function ReportDataPane(props: DataPaneProps) {
  const { t } = useTranslation();
  const [tab, setTab] = useState(0);
  const [fieldQuery, setFieldQuery] = useState("");
  const [objectDrafts, setObjectDrafts] = useState<Record<string, string>>({});
  const [expandedQueryId, setExpandedQueryId] = useState<string | false>(false);
  const [fieldsOpen, setFieldsOpen] = useState<Record<string, { dimensions: boolean; measures: boolean }>>(
    {}
  );

  const dataSources = useMemo(() => {
    const assigned = props.queries.filter(
      (query) => query.id && props.assignedQueryIds.includes(query.id)
    );
    if (assigned.length) return assigned;
    return props.queries.filter((query) => !!query.id);
  }, [props.queries, props.assignedQueryIds]);

  useEffect(() => {
    setObjectDrafts((prev) => {
      const next = { ...prev };
      for (const query of dataSources) {
        if (!query.id) continue;
        if (next[query.id] === undefined) next[query.id] = query.objectName || "";
      }
      return next;
    });
  }, [dataSources]);

  const fieldGroupOpen = (queryId: string, group: "dimensions" | "measures") =>
    fieldsOpen[queryId]?.[group] ?? true;

  const toggleFieldGroup = (queryId: string, group: "dimensions" | "measures") => {
    setFieldsOpen((prev) => ({
      ...prev,
      [queryId]: {
        dimensions: prev[queryId]?.dimensions ?? true,
        measures: prev[queryId]?.measures ?? true,
        [group]: !(prev[queryId]?.[group] ?? true),
      },
    }));
  };

  return (
    <Paper
      elevation={0}
      variant="outlined"
      className="no-print"
      sx={{
        width: 400,
        flexShrink: 0,
        display: "flex",
        flexDirection: "row",
        maxHeight: "100%",
        minHeight: 0,
        overflow: "hidden",
        bgcolor: "background.paper",
        borderRadius: 2,
      }}
    >
      <Stack
        spacing={0.75}
        sx={{
          width: 76,
          flexShrink: 0,
          p: 0.75,
          borderRight: 1,
          borderColor: "divider",
          bgcolor: (theme) =>
            theme.palette.mode === "dark" ? "background.default" : "grey.50",
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        {LEFT_NAV_TABS.map((item, index) => {
          const Icon = item.icon;
          const active = tab === index;
          return (
            <Tooltip key={item.id} title={t(item.labelKey)} placement="right">
              <Paper
                variant="outlined"
                onClick={() => setTab(index)}
                sx={{
                  width: "100%",
                  minHeight: 64,
                  py: 0.75,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.4,
                  px: 0.35,
                  cursor: "pointer",
                  borderColor: active ? "primary.main" : "divider",
                  bgcolor: active ? "background.paper" : "transparent",
                  boxShadow: active ? 1 : 0,
                  color: active ? "primary.main" : "text.secondary",
                  transition: "border-color 0.15s, background-color 0.15s, box-shadow 0.15s",
                  "&:hover": {
                    borderColor: "primary.main",
                    bgcolor: "action.hover",
                  },
                }}
              >
                <Icon sx={{ fontSize: 22, flexShrink: 0 }} />
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: 9,
                    lineHeight: 1.15,
                    fontWeight: active ? 700 : 500,
                    textAlign: "center",
                    maxWidth: "100%",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    wordBreak: "break-word",
                    px: 0.15,
                  }}
                >
                  {t(item.labelKey)}
                </Typography>
              </Paper>
            </Tooltip>
          );
        })}
      </Stack>

      <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {tab === 0 && (
          <Stack spacing={1.25} sx={{ overflow: "auto", p: 1.5, flex: 1, minHeight: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.4 }}>
              {t("reportDesigner.dataHint")}
            </Typography>
            {props.visualQueryId && (
              <Chip
                size="small"
                color="primary"
                variant="outlined"
                label={t("reportDesigner.visualBoundQuery", {
                  name:
                    props.queries.find((query) => query.id === props.visualQueryId)?.name ||
                    props.visualQueryId,
                })}
              />
            )}

            {!dataSources.length && (
              <Typography variant="caption" color="warning.main">
                {t("reportDesigner.noDataSources")}
              </Typography>
            )}

            <Stack spacing={1}>
              {dataSources.map((query) => {
                const queryId = query.id!;
                const selected = expandedQueryId === queryId;
                const boundToVisual = props.visualQueryId === queryId;
                const schema = selected ? props.schemasByQueryId[queryId] : undefined;
                const columns = selected
                  ? (schema?.columns || []).filter(
                      (column) =>
                        !fieldQuery.trim() ||
                        column.name.toLowerCase().includes(fieldQuery.trim().toLowerCase())
                    )
                  : [];
                const dimensions = columns.filter((column) => column.role !== "measure");
                const measures = columns.filter((column) => column.role === "measure");

                const renderFieldList = (items: QueryColumnSchema[]) => (
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 0.75,
                      alignContent: "flex-start",
                      px: 1,
                      pb: 1,
                    }}
                  >
                    {items.map((column) => (
                      <Chip
                        key={`${queryId}-${column.name}`}
                        label={column.name}
                        size="small"
                        color={column.role === "measure" ? "primary" : "default"}
                        draggable
                        onDragStart={(event) => {
                          event.dataTransfer.setData(
                            FIELD_DRAG_MIME,
                            encodeDragField(column, queryId)
                          );
                          event.dataTransfer.effectAllowed = "copy";
                        }}
                        onClick={() => props.onUseField(column, queryId)}
                        sx={{ cursor: "grab", maxWidth: "100%" }}
                      />
                    ))}
                  </Box>
                );

                return (
                  <Accordion
                    key={queryId}
                    disableGutters
                    elevation={0}
                    expanded={selected}
                    onChange={(_, expanded) => {
                      setExpandedQueryId(expanded ? queryId : false);
                      if (expanded) {
                        props.onSelectQuery(queryId);
                        setFieldQuery("");
                      }
                    }}
                    slotProps={{ transition: { unmountOnExit: true } }}
                    sx={{
                      border: 1,
                      borderColor: selected || boundToVisual ? "primary.main" : "divider",
                      borderRadius: "8px !important",
                      bgcolor: selected ? "action.selected" : "background.paper",
                      "&:before": { display: "none" },
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />}>
                      <Stack spacing={0.25} sx={{ minWidth: 0, pr: 1 }}>
                        <Typography variant="body2" fontWeight={700} noWrap>
                          {query.name || queryId}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {[query.dataSource?.sourceType, query.objectName].filter(Boolean).join(" · ") ||
                            t("reportDesigner.query")}
                        </Typography>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                      <Stack spacing={1.25}>
                        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.35 }}>
                          {t("reportDesigner.bindHint")}
                        </Typography>
                        <AppTextField
                          size="small"
                          fullWidth
                          label={t("reportDesigner.objectName")}
                          value={objectDrafts[queryId] ?? query.objectName ?? ""}
                          onChange={(event) =>
                            setObjectDrafts((prev) => ({ ...prev, [queryId]: event.target.value }))
                          }
                          onBlur={() => {
                            const draft = (objectDrafts[queryId] ?? "").trim();
                            if (draft !== (query.objectName || "")) {
                              props.onExplore?.(queryId, draft);
                            }
                          }}
                          helperText={t("reportDesigner.exploreHint")}
                          FormHelperTextProps={{ sx: { mx: 0, mt: 0.75, lineHeight: 1.35 } }}
                        />
                        <AppTextField
                          size="small"
                          fullWidth
                          label={t("reportDesigner.fieldSearch")}
                          value={fieldQuery}
                          onChange={(event) => setFieldQuery(event.target.value)}
                        />
                        {schema?.loading && (
                          <Stack direction="row" spacing={1} alignItems="center">
                            <CircularProgress size={14} />
                            <Typography variant="caption" color="text.secondary">
                              {t("reportDesigner.catalogLoading")}
                            </Typography>
                          </Stack>
                        )}
                        {schema?.note && (
                          <Typography variant="caption" color="warning.main">
                            {schema.note}
                          </Typography>
                        )}

                        <List dense disablePadding sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}>
                          <ListItemButton onClick={() => toggleFieldGroup(queryId, "dimensions")} sx={{ py: 0.75 }}>
                            <ListItemText
                              primary={t("reportDesigner.dimensionsGroup", { count: dimensions.length })}
                              primaryTypographyProps={{ variant: "body2", fontWeight: 700 }}
                            />
                            {fieldGroupOpen(queryId, "dimensions") ? (
                              <ExpandLessIcon fontSize="small" />
                            ) : (
                              <ExpandMoreIcon fontSize="small" />
                            )}
                          </ListItemButton>
                          <Collapse in={fieldGroupOpen(queryId, "dimensions")} timeout="auto" unmountOnExit>
                            {dimensions.length ? (
                              renderFieldList(dimensions)
                            ) : (
                              <Typography variant="caption" color="text.secondary" sx={{ px: 1, pb: 1, display: "block" }}>
                                {t("reportDesigner.noFields")}
                              </Typography>
                            )}
                          </Collapse>

                          <Divider />

                          <ListItemButton onClick={() => toggleFieldGroup(queryId, "measures")} sx={{ py: 0.75 }}>
                            <ListItemText
                              primary={t("reportDesigner.measuresGroup", { count: measures.length })}
                              primaryTypographyProps={{ variant: "body2", fontWeight: 700 }}
                            />
                            {fieldGroupOpen(queryId, "measures") ? (
                              <ExpandLessIcon fontSize="small" />
                            ) : (
                              <ExpandMoreIcon fontSize="small" />
                            )}
                          </ListItemButton>
                          <Collapse in={fieldGroupOpen(queryId, "measures")} timeout="auto" unmountOnExit>
                            {measures.length ? (
                              renderFieldList(measures)
                            ) : (
                              <Typography variant="caption" color="text.secondary" sx={{ px: 1, pb: 1, display: "block" }}>
                                {t("reportDesigner.noFields")}
                              </Typography>
                            )}
                          </Collapse>
                        </List>
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Stack>
          </Stack>
        )}
        {tab === 1 && (
          <Box sx={{ flex: 1, minHeight: 0, minWidth: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
            <WidgetPalette
              embedded
              widgets={props.widgets}
              placedWidgetIds={props.placedWidgetIds}
              onAddWidget={props.onAddWidget}
            />
          </Box>
        )}
        {tab === 2 && (
          <Box sx={{ p: 1.25, flex: 1, minHeight: 0, minWidth: 0, overflowX: "hidden", overflowY: "auto" }}>
            <DecorationPaletteHeader />
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              {t("dashboard.decor.paletteHint")}
            </Typography>
            <DecorationPalette onAddDecoration={props.onAddDecoration} />
          </Box>
        )}
        {tab === 3 && (
          <Box sx={{ flex: 1, minHeight: 0, minWidth: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
            <AssignedQueriesPanel
              assignedQueryIds={props.assignedQueryIds}
              queries={props.queries}
              decorations={props.decorations}
              onAssign={props.onAssignQuery}
              onUnassign={props.onUnassignQuery}
            />
          </Box>
        )}
      </Box>
    </Paper>
  );
}

type BuildPaneProps = {
  dashboard: ReportDashboard;
  visual?: DashboardVisualPlacement;
  pages: { id: string; name: string }[];
  activePageId?: string;
  focusToken?: number;
  onAddVisual: (vizType: string) => void;
  onChangeVisual: (visual: DashboardVisualPlacement) => void;
  onEditVisual?: (visualId: string, options?: { tab?: number }) => void;
  onAddFilter: (scope: "visual" | "page" | "report", filter: DashboardFilter) => void;
  onSaveBookmark: (name: string) => void;
  onApplyBookmark: (bookmark: DashboardBookmark) => void;
  mobileStacked?: boolean;
  onToggleMobile: () => void;
};

export function ReportBuildPane({
  dashboard,
  visual,
  focusToken = 0,
  onAddVisual,
  onChangeVisual,
  onEditVisual,
  onAddFilter,
  onSaveBookmark,
  onApplyBookmark,
  mobileStacked,
  onToggleMobile,
}: BuildPaneProps) {
  const { t } = useTranslation();
  const [tab, setTab] = useState(0);
  const [catalog, setCatalog] = useState<VizTypeSpec[]>([]);
  const [bookmarkName, setBookmarkName] = useState("");
  const [filterField, setFilterField] = useState("");
  const [filterValue, setFilterValue] = useState("");
  const [formatDraft, setFormatDraft] = useState<VisualFormat>({});
  const [formatDirty, setFormatDirty] = useState(false);

  useEffect(() => {
    dashboardService.vizCatalog().then((response) => setCatalog(response.types || [])).catch(console.error);
  }, []);

  useEffect(() => {
    if (visual?.id || focusToken > 0) {
      setTab(0);
    }
  }, [visual?.id, focusToken]);

  useEffect(() => {
    setFormatDraft(visual?.format ? structuredClone(visual.format) : {});
    setFormatDirty(false);
  }, [visual?.id]);

  const spec = catalog.find((item) => item.vizType === visual?.vizType);

  const applyFormatDraft = () => {
    if (!visual) return;
    onChangeVisual({ ...visual, format: formatDraft });
    setFormatDirty(false);
  };

  const resetFormatDraft = () => {
    setFormatDraft(visual?.format ? structuredClone(visual.format) : {});
    setFormatDirty(false);
  };

  return (
    <Paper
      elevation={0}
      variant="outlined"
      className="no-print"
      sx={{
        width: 320,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        maxHeight: "100%",
        overflow: "hidden",
        bgcolor: "background.paper",
        borderRadius: 2,
      }}
    >
      <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="fullWidth">
        <Tab label={t("reportDesigner.build")} />
        <Tab label={t("reportDesigner.format")} />
        <Tab label={t("reportDesigner.filters")} />
      </Tabs>
      <Box sx={{ overflow: "auto", p: 1.25 }}>
        {tab === 0 && (
          <Stack spacing={1}>
            <Typography variant="caption" color="text.secondary">
              {t("reportDesigner.gallery")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t("reportDesigner.galleryDragHint")}
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                gap: 0.75,
                width: "100%",
                maxWidth: "100%",
                overflow: "hidden",
              }}
            >
              {catalog.filter((item) => item.supported).map((item) => {
                const Icon = vizIcon(item.vizType);
                const selected = visual?.vizType === item.vizType;
                return (
                  <Tooltip key={item.vizType} title={item.label} placement="top" enterDelay={300}>
                    <Paper
                      variant="outlined"
                      draggable
                      onDragStart={(event) => {
                        event.dataTransfer.setData(VISUAL_DRAG_MIME, item.vizType);
                        event.dataTransfer.effectAllowed = "copy";
                      }}
                      onClick={() => onAddVisual(item.vizType)}
                      sx={{
                        aspectRatio: "1 / 1",
                        width: "100%",
                        maxWidth: "100%",
                        minWidth: 0,
                        boxSizing: "border-box",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.35,
                        px: 0.5,
                        overflow: "hidden",
                        cursor: "grab",
                        borderColor: selected ? "primary.main" : "divider",
                        bgcolor: selected ? "action.selected" : "background.paper",
                        transition: "border-color 0.15s, background-color 0.15s, box-shadow 0.15s",
                        "&:hover": {
                          borderColor: "primary.main",
                          bgcolor: "action.hover",
                          boxShadow: 1,
                        },
                      }}
                    >
                      <Icon
                        sx={{
                          fontSize: 24,
                          flexShrink: 0,
                          color: selected ? "primary.main" : "text.secondary",
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: 10,
                          lineHeight: 1.1,
                          textAlign: "center",
                          color: selected ? "primary.main" : "text.secondary",
                          fontWeight: selected ? 700 : 500,
                          maxWidth: "100%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          px: 0.25,
                        }}
                      >
                        {item.label}
                      </Typography>
                    </Paper>
                  </Tooltip>
                );
              })}
            </Box>
            {visual ? (
              <>
                <Divider />
                <Typography variant="subtitle2" fontWeight={700}>
                  {t("reportDesigner.selectedVisual", { type: visual.vizType || "" })}
                </Typography>
                {visual.title && (
                  <Typography variant="caption" color="text.secondary">
                    {visual.title}
                  </Typography>
                )}
                {spec && (
                  <Stack spacing={0.75}>
                    {spec.wells.map((well) => {
                      const fields = visual.wells?.[well.id] || [];
                      if (!fields.length) return null;
                      return (
                        <Box key={well.id}>
                          <Typography variant="caption" fontWeight={700} color="text.secondary">
                            {well.label}
                          </Typography>
                          <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.5} sx={{ mt: 0.35 }}>
                            {fields.map((item, index) => (
                              <Chip
                                key={`${item.field}-${index}`}
                                size="small"
                                label={item.aggregate ? `${item.aggregate}(${item.field})` : item.field}
                              />
                            ))}
                          </Stack>
                        </Box>
                      );
                    })}
                  </Stack>
                )}
                <AppButton
                  size="small"
                  variant="contained"
                  startIcon={<DriveFileRenameOutlineOutlinedIcon />}
                  onClick={() => visual.id && onEditVisual?.(visual.id)}
                  disabled={!visual.id || !onEditVisual}
                >
                  {t("reportDesigner.editVisual")}
                </AppButton>
                <Typography variant="caption" color="text.secondary">
                  {t("reportDesigner.editVisualDialogHint")}
                </Typography>
              </>
            ) : (
              <Typography variant="caption" color="text.secondary">
                {t("reportDesigner.selectVisualHint")}
              </Typography>
            )}
          </Stack>
        )}
        {tab === 1 && (
          <Stack spacing={1.25}>
            {visual ? (
              <>
                <VisualFormatControls
                  value={formatDraft}
                  onChange={(format) => {
                    setFormatDraft(format);
                    setFormatDirty(true);
                  }}
                  showMobileToggle
                  mobileStacked={mobileStacked}
                  onToggleMobile={onToggleMobile}
                />
                <Stack direction="row" spacing={0.75}>
                  <AppButton
                    size="small"
                    variant="outlined"
                    onClick={resetFormatDraft}
                    disabled={!formatDirty}
                  >
                    {t("common.cancel")}
                  </AppButton>
                  <AppButton
                    size="small"
                    variant="contained"
                    onClick={applyFormatDraft}
                    disabled={!formatDirty}
                  >
                    {t("common.apply")}
                  </AppButton>
                </Stack>
                <AppButton
                  size="small"
                  variant="text"
                  startIcon={<DriveFileRenameOutlineOutlinedIcon />}
                  onClick={() => visual.id && onEditVisual?.(visual.id, { tab: 1 })}
                  disabled={!visual.id || !onEditVisual}
                >
                  {t("reportDesigner.editVisual")}
                </AppButton>
              </>
            ) : (
              <>
                <Typography variant="caption" color="text.secondary">
                  {t("reportDesigner.selectVisualHint")}
                </Typography>
                <AppButton
                  size="small"
                  variant={mobileStacked ? "contained" : "outlined"}
                  color="secondary"
                  startIcon={<PhoneAndroidOutlinedIcon />}
                  onClick={onToggleMobile}
                >
                  {t("reportDesigner.mobile")}
                </AppButton>
              </>
            )}
          </Stack>
        )}
        {tab === 2 && (
          <Stack spacing={1}>
            <AppTextField size="small" label={t("reportDesigner.filterField")} value={filterField} onChange={(event) => setFilterField(event.target.value)} />
            <AppTextField size="small" label={t("reportDesigner.filterValue")} value={filterValue} onChange={(event) => setFilterValue(event.target.value)} />
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              <AppButton size="small" onClick={() => onAddFilter("visual", { field: filterField, operator: "eq", values: [filterValue] })}>
                {t("reportDesigner.scopeVisual")}
              </AppButton>
              <AppButton size="small" onClick={() => onAddFilter("page", { field: filterField, operator: "eq", values: [filterValue] })}>
                {t("reportDesigner.scopePage")}
              </AppButton>
              <AppButton size="small" onClick={() => onAddFilter("report", { field: filterField, operator: "eq", values: [filterValue] })}>
                {t("reportDesigner.scopeReport")}
              </AppButton>
            </Stack>
            {(visual?.filters || []).map((filter, index) => (
              <Typography key={`v-${index}`} variant="caption">
                {t("reportDesigner.filterVisualLine", { field: filter.field, value: filter.values?.join(", ") })}
              </Typography>
            ))}
            {(dashboard.filters || []).map((filter, index) => (
              <Typography key={`r-${index}`} variant="caption">
                {t("reportDesigner.filterReportLine", { field: filter.field, value: filter.values?.join(", ") })}
              </Typography>
            ))}
            <Divider />
            <AppTextField size="small" label={t("reportDesigner.bookmarkName")} value={bookmarkName} onChange={(event) => setBookmarkName(event.target.value)} />
            <AppButton
              size="small"
              startIcon={<BookmarkBorderOutlinedIcon />}
              onClick={() => { onSaveBookmark(bookmarkName); setBookmarkName(""); }}
            >
              {t("reportDesigner.bookmark")}
            </AppButton>
            {(dashboard.bookmarks || []).map((bookmark) => (
              <Chip key={bookmark.id} label={bookmark.name} onClick={() => onApplyBookmark(bookmark)} />
            ))}
          </Stack>
        )}
      </Box>
    </Paper>
  );
}

function RibbonGroup({ children }: { children: ReactNode }) {
  return (
    <Stack direction="row" spacing={0.25} alignItems="center" sx={{ px: 0.5 }}>
      {children}
    </Stack>
  );
}

export function ReportRibbon({
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  onZoomIn,
  onZoomOut,
  onFit,
  onAlignLeft,
  onAlignCenter,
  onAlignTop,
  onFront,
  onBack,
  onDuplicate,
}: {
  onUndo: () => void;
  onRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onFit: () => void;
  onAlignLeft: () => void;
  onAlignCenter: () => void;
  onAlignTop: () => void;
  onFront: () => void;
  onBack: () => void;
  onDuplicate: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Paper
      elevation={0}
      variant="outlined"
      className="no-print"
      sx={{
        mt: 2.5,
        mb: 2,
        px: 1,
        py: 0.75,
        borderRadius: 2,
        bgcolor: (theme) =>
          theme.palette.mode === "dark" ? "background.default" : "grey.50",
        borderColor: "divider",
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 0.5,
        rowGap: 0.75,
      }}
    >
      <RibbonGroup>
        <AppIconButton size="small" color="primary" disabled={!canUndo} onClick={onUndo} title={t("reportDesigner.undo")}>
          <UndoOutlinedIcon fontSize="small" />
        </AppIconButton>
        <AppIconButton size="small" color="primary" disabled={!canRedo} onClick={onRedo} title={t("reportDesigner.redo")}>
          <RedoOutlinedIcon fontSize="small" />
        </AppIconButton>
      </RibbonGroup>
      <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />
      <RibbonGroup>
        <AppIconButton size="small" color="secondary" onClick={onZoomOut} title={t("reportDesigner.zoomOut")}>
          <ZoomOutOutlinedIcon fontSize="small" />
        </AppIconButton>
        <AppIconButton size="small" color="secondary" onClick={onZoomIn} title={t("reportDesigner.zoomIn")}>
          <ZoomInOutlinedIcon fontSize="small" />
        </AppIconButton>
        <AppIconButton size="small" color="secondary" onClick={onFit} title={t("reportDesigner.fit")}>
          <FitScreenOutlinedIcon fontSize="small" />
        </AppIconButton>
      </RibbonGroup>
      <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />
      <RibbonGroup>
        <AppIconButton size="small" color="info" onClick={onAlignLeft} title={t("reportDesigner.alignLeft")}>
          <AlignHorizontalLeftOutlinedIcon fontSize="small" />
        </AppIconButton>
        <AppIconButton size="small" color="info" onClick={onAlignCenter} title={t("reportDesigner.alignCenter")}>
          <AlignHorizontalCenterOutlinedIcon fontSize="small" />
        </AppIconButton>
        <AppIconButton size="small" color="info" onClick={onAlignTop} title={t("reportDesigner.alignTop")}>
          <AlignVerticalTopOutlinedIcon fontSize="small" />
        </AppIconButton>
      </RibbonGroup>
      <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />
      <RibbonGroup>
        <AppIconButton size="small" color="warning" onClick={onFront} title={t("reportDesigner.front")}>
          <FlipToFrontOutlinedIcon fontSize="small" />
        </AppIconButton>
        <AppIconButton size="small" color="warning" onClick={onBack} title={t("reportDesigner.back")}>
          <FlipToBackOutlinedIcon fontSize="small" />
        </AppIconButton>
        <AppIconButton size="small" color="success" onClick={onDuplicate} title={t("reportDesigner.duplicate")}>
          <ContentCopyOutlinedIcon fontSize="small" />
        </AppIconButton>
      </RibbonGroup>
    </Paper>
  );
}

export function ReportPageStrip({
  pages,
  editable = false,
  onSelect,
  onAdd,
  onRename,
  onRemove,
}: {
  pages: DashboardPage[];
  editable?: boolean;
  onSelect: (pageId: string) => void;
  onAdd: () => void;
  onRename: (pageId: string, name: string) => void;
  onRemove?: (pageId: string) => void;
}) {
  const { t } = useTranslation();
  const [renamingId, setRenamingId] = useState<string>();
  const [renameDraft, setRenameDraft] = useState("");
  const activeId = pages.find((page) => !page.hidden)?.id || pages[0]?.id;

  const beginRename = (page: DashboardPage) => {
    if (!editable || !page.id) return;
    setRenamingId(page.id);
    setRenameDraft(page.name || "");
  };

  const commitRename = () => {
    if (!renamingId) return;
    const trimmed = renameDraft.trim();
    if (trimmed) onRename(renamingId, trimmed);
    setRenamingId(undefined);
    setRenameDraft("");
  };

  const cancelRename = () => {
    setRenamingId(undefined);
    setRenameDraft("");
  };

  return (
    <Paper
      elevation={0}
      className="no-print"
      sx={{
        mt: 2,
        borderTop: 1,
        borderColor: "divider",
        borderRadius: 0,
        bgcolor: (theme) =>
          theme.palette.mode === "dark" ? "background.default" : "grey.100",
        display: "flex",
        alignItems: "stretch",
        overflowX: "auto",
        minHeight: 40,
      }}
    >
      <Stack direction="row" alignItems="stretch" sx={{ flex: 1, minWidth: 0 }}>
        {pages.map((page, index) => {
          const active = page.id === activeId;
          const renaming = renamingId === page.id;
          return (
            <Box
              key={page.id || index}
              onClick={() => {
                if (!renaming && page.id) onSelect(page.id);
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                px: 1.25,
                py: 0.75,
                minWidth: 120,
                maxWidth: 220,
                cursor: renaming ? "text" : "pointer",
                borderRight: 1,
                borderColor: "divider",
                bgcolor: active ? "background.paper" : "transparent",
                borderTop: active ? 2 : 0,
                borderTopColor: active ? "primary.main" : "transparent",
                color: active ? "text.primary" : "text.secondary",
                "&:hover": {
                  bgcolor: active ? "background.paper" : "action.hover",
                },
              }}
            >
              <InsertDriveFileOutlinedIcon
                sx={{ fontSize: 16, color: active ? "primary.main" : "text.disabled", flexShrink: 0 }}
              />
              {renaming ? (
                <Stack direction="row" alignItems="center" spacing={0.25} sx={{ minWidth: 0, flex: 1 }} onClick={(e) => e.stopPropagation()}>
                  <InputBase
                    autoFocus
                    value={renameDraft}
                    onChange={(event) => setRenameDraft(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        commitRename();
                      }
                      if (event.key === "Escape") cancelRename();
                    }}
                    sx={{
                      fontSize: 13,
                      flex: 1,
                      bgcolor: "background.paper",
                      px: 0.75,
                      borderRadius: 0.75,
                      border: 1,
                      borderColor: "primary.main",
                    }}
                  />
                  <AppIconButton size="small" color="success" title={t("common.save")} onClick={commitRename}>
                    <CheckOutlinedIcon sx={{ fontSize: 16 }} />
                  </AppIconButton>
                  <AppIconButton size="small" title={t("common.cancel")} onClick={cancelRename}>
                    <CloseOutlinedIcon sx={{ fontSize: 16 }} />
                  </AppIconButton>
                </Stack>
              ) : (
                <>
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{ flex: 1, fontWeight: active ? 700 : 500, fontSize: 13 }}
                    title={page.name || t("reportDesigner.page")}
                  >
                    {page.name || t("reportDesigner.page")}
                  </Typography>
                  {editable && active && (
                    <Stack direction="row" spacing={0} onClick={(e) => e.stopPropagation()}>
                      <AppIconButton
                        size="small"
                        color="primary"
                        title={t("reportDesigner.pageRename")}
                        onClick={() => beginRename(page)}
                      >
                        <DriveFileRenameOutlineOutlinedIcon sx={{ fontSize: 16 }} />
                      </AppIconButton>
                      {pages.length > 1 && onRemove && page.id && (
                        <AppIconButton
                          size="small"
                          color="error"
                          title={t("reportDesigner.removePage")}
                          onClick={() => onRemove(page.id as string)}
                        >
                          <DeleteOutlineOutlinedIcon sx={{ fontSize: 16 }} />
                        </AppIconButton>
                      )}
                    </Stack>
                  )}
                </>
              )}
            </Box>
          );
        })}
      </Stack>
      {editable && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            px: 0.75,
            borderLeft: 1,
            borderColor: "divider",
            bgcolor: (theme) =>
              theme.palette.mode === "dark" ? "background.paper" : "grey.50",
          }}
        >
          <AppIconButton size="small" color="primary" onClick={onAdd} title={t("reportDesigner.addPage")}>
            <AddOutlinedIcon fontSize="small" />
          </AppIconButton>
        </Box>
      )}
    </Paper>
  );
}

export function useQuerySchema(queryId?: string, objectName?: string) {
  const [columns, setColumns] = useState<QueryColumnSchema[]>([]);
  const [note, setNote] = useState<string>();
  useEffect(() => {
    if (!queryId) {
      setColumns([]);
      setNote(undefined);
      return;
    }
    dashboardQueryService.schema(queryId, objectName ? { objectName } : {}).then((schema) => {
      setColumns(schema.columns || []);
      setNote(schema.note);
    }).catch((error: Error) => setNote(error.message));
  }, [queryId, objectName]);
  return { columns, note };
}

export function useQuerySchemas(queries: Array<{ id?: string; objectName?: string }>) {
  const [schemasByQueryId, setSchemasByQueryId] = useState<Record<string, QuerySchemaState>>({});
  const schemaKey = useMemo(
    () =>
      queries
        .filter((query) => query.id)
        .map((query) => `${query.id}:${query.objectName || ""}`)
        .sort()
        .join("|"),
    [queries]
  );

  useEffect(() => {
    const targets = queries.filter((query): query is { id: string; objectName?: string } => !!query.id);
    if (!targets.length) {
      setSchemasByQueryId({});
      return;
    }
    let cancelled = false;
    setSchemasByQueryId((prev) => {
      const next: Record<string, QuerySchemaState> = {};
      for (const query of targets) {
        next[query.id] = { columns: prev[query.id]?.columns || [], note: prev[query.id]?.note, loading: true };
      }
      return next;
    });
    Promise.all(
      targets.map(async (query) => {
        try {
          const schema = await dashboardQueryService.schema(
            query.id,
            query.objectName ? { objectName: query.objectName } : {}
          );
          return [query.id, { columns: schema.columns || [], note: schema.note, loading: false }] as const;
        } catch (error) {
          return [
            query.id,
            { columns: [], note: error instanceof Error ? error.message : String(error), loading: false },
          ] as const;
        }
      })
    ).then((entries) => {
      if (!cancelled) setSchemasByQueryId(Object.fromEntries(entries));
    });
    return () => {
      cancelled = true;
    };
  }, [schemaKey]);

  return schemasByQueryId;
}
